import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {catchError, concatMap, map, tap, withLatestFrom} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import {AnalyticsEvents, AnalyticsService} from '../../../../../services/analytics.service';
import {CreateBundleService} from '../../../../../services/bundle/create-bundle.service';
import {CatalogApiService} from '../../../../../services/catalog-api.service';
import {selectSelectedOrganizationId} from '../../../../../state/router/router.selectors';
import {MISSING_CATALOG_ERROR_MESSAGE} from '../../../../catalog-list/store/catalog.constants';
import {CreateBundleActions} from './create-bundle.actions';
@Injectable()
export class CreateBundleEffects {
  loadCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateBundleActions.loadCatalogs),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      concatMap(([, organizationId]) => {
        if (!organizationId) {
          return of(CreateBundleActions.loadCatalogsFailure());
        }

        return this.catalogApi.getAllCatalogsByOrganizationId(organizationId).pipe(
          map(catalogs => CreateBundleActions.loadCatalogsSuccess({catalogs})),
          catchError(() => of(CreateBundleActions.loadCatalogsFailure()))
        );
      })
    )
  );

  createBundle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CreateBundleActions.createBundle),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      concatMap(([{form, active, images, productIds}, organizationId]) => {
        if (!organizationId) {
          return of(
            CreateBundleActions.createBundleFailed({
              error: {message: 'Organization context could not be loaded. Please refresh and try again.'},
            })
          );
        }

        return this.createBundleService.createBundleFromForm(form, organizationId, active, productIds, images).pipe(
          tap(bundle => {
            this.analytics.trackAction(AnalyticsEvents.AddBundle, {
              bundle_id: String(bundle.id),
              product_count: productIds.length,
              event_type: 1,
            });
          }),
          map(bundle => CreateBundleActions.createBundleSuccess({bundleId: String(bundle.id)})),
          catchError(() =>
            of(
              CreateBundleActions.createBundleFailed({
                error: {message: 'An error occurred, please refresh and try again.'},
              })
            )
          )
        );
      })
    );
  });

  createBundleSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CreateBundleActions.createBundleSuccess),
        tap(() => {
          this.toast.done('Successfully created bundle');
        })
      );
    },
    {dispatch: false}
  );

  createBundleFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CreateBundleActions.createBundleFailed),
        tap(({error}) => {
          this.toast.critical(error.message);
        })
      );
    },
    {dispatch: false}
  );

  loadBundlesByCatalogId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateBundleActions.loadBundlesByCatalogId),
      concatMap(({catalogId}) => {
        if (!catalogId) {
          return of(
            CreateBundleActions.loadBundlesByCatalogIdFailed({
              error: {message: MISSING_CATALOG_ERROR_MESSAGE},
            })
          );
        }
        return this.catalogApi.getBundlesByCatalogId(catalogId).pipe(
          map(bundles => CreateBundleActions.loadBundlesByCatalogIdSuccess({bundles})),
          catchError(() =>
            of(
              CreateBundleActions.loadBundlesByCatalogIdFailed({
                error: {message: 'An error occurred, please refresh and try again.'},
              })
            )
          )
        );
      })
    )
  );

  loadBundlesByCatalogIdFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateBundleActions.loadBundlesByCatalogIdFailed),
        tap(({error}) => {
          this.toast.critical(error.message);
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private createBundleService: CreateBundleService,
    private toast: SeFeToastService,
    private store: Store,
    private catalogApi: CatalogApiService,
    private analytics: AnalyticsService
  ) {}
}
